import {
  BadgeCheck,
  Chrome,
  ClipboardCheck,
  Download,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LogIn,
  MailCheck,
  PlayCircle,
} from "lucide-react";
import type {
  GuideSlide,
  GuideStep,
} from "@/components/training/SlideGuide";

export const LEGITSCRIPT_ONBOARDING_CHECKLIST = [
  {
    id: "invited-account",
    label: "I signed in with the work email that received the LegitScript invitation.",
  },
  {
    id: "analyst-workspace",
    label: "LegitScript branding, My Work, and Classification queues are visible.",
  },
  {
    id: "google-workspace",
    label: "Google Workspace shows Connected in Settings → Tools.",
  },
  {
    id: "browser-agent",
    label: "The Browser Agent is installed in Chrome and shows Connected in Longhand.",
  },
  {
    id: "no-personal-key",
    label: "I did not enter a personal API key during setup.",
  },
  {
    id: "practice-result",
    label: "I recorded the practice run name or ID and its final state.",
  },
  {
    id: "lead-handoff",
    label: "I sent my lead the review status and any exact error, screenshot, or open question.",
  },
  {
    id: "lead-approval",
    label: "My lead approved the next step before I start independent queue work.",
  },
] as const;

export const LEGITSCRIPT_ONBOARDING_STEPS: GuideStep[] = [
  {
    id: "prepare",
    title: "Prepare your invitation",
    description: "Confirm the account, queue, and stopping point for your first session.",
    duration: "3 min",
    whyItMatters:
      "The invitation is tied to one email address. Before you open Longhand, confirm the account you will use, the practice queue, and where the first run should stop.",
    actions: [
      "Find the Longhand invitation to the LegitScript organization and keep it open.",
      "Confirm the exact work email address that received the invitation. You must use that same address when you sign in.",
      "Ask your LegitScript onboarding lead which queue is approved for your supervised practice run.",
      "Confirm whether the practice run is allowed to submit to the LegitScript database or must stop at review.",
    ],
    checkpoint:
      "You can name the invited work email, the designated practice queue, the person supervising the session, and the approved stopping point for the first run.",
    icon: MailCheck,
    deliverables: [
      "Invitation email ready",
      "Correct sign-in email confirmed",
      "Practice queue named",
      "First-run stop rule confirmed",
    ],
    note:
      "Do not use a personal email or a different work alias. Longhand accepts the pending organization invitation when the invited identity signs in.",
  },
  {
    id: "install",
    title: "Install and open Longhand",
    description: "Open Longhand from the invitation. Use the download page only if the app is not installed.",
    duration: "5 min",
    whyItMatters:
      "The invitation opens the desktop app and carries the sign-in context with it. If Longhand is not installed, download it, then return to the invitation.",
    actions: [
      "Click Open Longhand in the invitation email.",
      "If your browser asks permission to launch Longhand, choose Allow or Open Longhand.",
      {
        text: "If the app is not installed, use the Download option and install the correct version:",
        subSteps: [
          "macOS: install the Apple Silicon version, then open Longhand from Applications.",
          "Windows: run the Windows installer, then open Longhand from the Start menu.",
        ],
      },
      "Return to the invitation email and click Open Longhand again if the app opened without the invitation context.",
    ],
    checkpoint:
      "Longhand opens as a desktop app on your computer, and clicking the invitation's Open Longhand button brings the app to the foreground.",
    icon: Download,
    images: [
      {
        src: "/legitscript-longhand-onboarding/download-longhand.png",
        alt: "Longhand download page with buttons for Mac and Windows",
        label: "Download fallback",
        caption:
          "Use the download page only when the invitation cannot open the installed app.",
      },
    ],
    links: [
      {
        label: "Download Longhand for macOS or Windows",
        href: "https://www.getlonghand.com/download",
        external: true,
      },
    ],
    reference: {
      title: "Installation paths",
      headers: ["Platform", "Install", "Open from"],
      rows: [
        ["macOS", "Open the downloaded disk image and install Longhand", "Applications"],
        ["Windows", "Run the downloaded installer", "Start menu"],
      ],
    },
  },
  {
    id: "join",
    title: "Join with your invited identity",
    description: "Sign in with the same work email that received the invitation.",
    duration: "5 min",
    whyItMatters:
      "LegitScript has already set up the workspace for you. Signing in with the invited email connects this installation to the right organization.",
    actions: [
      "Choose Sign in with Longhand when the app asks how you want to set up access.",
      "Use the exact work email address that received the LegitScript invitation.",
      "Complete the browser sign-in and allow the browser to return to Longhand.",
      "Wait for Longhand to land on Home. A LegitScript organization member should skip the general self-serve setup wizard.",
    ],
    checkpoint:
      "The sign-in finishes, Longhand returns to Home, and you are not being asked to configure a personal AI provider as part of a general first-run wizard.",
    icon: LogIn,
    images: [
      {
        src: "/legitscript-longhand-onboarding/analyst-home.png",
        alt: "Longhand Home screen with the LegitScript organization experience loaded",
        label: "Successful organization sign-in",
        caption:
          "The LegitScript wordmark and Home screen confirm the organization experience loaded. Example metrics are obscured.",
      },
    ],
    reference: {
      title: "What each outcome means",
      headers: ["What you see", "What to do"],
      rows: [
        ["Home with the LegitScript experience", "Continue to the workspace check."],
        ["A browser sign-in loop", "Stop and capture the exact browser or app message."],
        ["The general Connect an AI Provider wizard", "Do not add a personal key; stop and contact your onboarding lead."],
        ["A different organization", "Stop. Confirm the invited email before trying again."],
      ],
    },
    note:
      "Do not paste a LegitScript, Anthropic, OpenAI, or other organization secret into the app. LegitScript access comes from your signed-in workspace.",
  },
  {
    id: "connect-workspace",
    title: "Connect your Google Workspace",
    description: "Connect the approved work account Longhand will use during onboarding.",
    duration: "5 min",
    whyItMatters:
      "Longhand uses this connection to work with Gmail, Calendar, Drive, Sheets, and Docs. Set it up now so your first workflow doesn't stop when it needs a Google tool or open the wrong account.",
    actions: [
      "Open Longhand Settings, then choose Tools.",
      "Under Native Integrations, choose Google Workspace.",
      "Choose Connect or Sign in. Use the Google Workspace account your LegitScript lead approved for this work.",
      "Review Google's permission request, finish signing in, and return to Longhand.",
      "Look for Connected. Then open the connection details and run Test Connection if you see it.",
    ],
    checkpoint:
      "Google Workspace shows Connected in Settings → Tools. Test Connection also succeeds for the approved work account.",
    icon: Link2,
    images: [
      {
        src: "/legitscript-longhand-onboarding/google-workspace-connected.png",
        alt: "Longhand Settings showing a connected Google Workspace account and available Gmail, Calendar, Drive, Sheets, and Docs tools",
        label: "Settings → Tools",
        caption:
          "You're ready when the approved work account shows Connected. We've hidden the example email address.",
      },
    ],
    reference: {
      title: "Make sure the right account is connected",
      headers: ["Expected", "Stop signal"],
      rows: [
        ["The work Google account your lead approved", "A personal account or another work identity"],
        ["Google Workspace says Connected", "Sign-in required, disconnected, or authorization error"],
        ["All five Google services are listed", "A service is missing after Test Connection"],
      ],
    },
    note:
      "Not sure which account to use? Stop and ask your onboarding lead. Don't connect a personal account or guess.",
  },
  {
    id: "connect-browser-agent",
    title: "Install and connect the Browser Agent",
    description: "Install the Longhand Agent Chrome extension, then make sure Longhand can see it.",
    duration: "7 min",
    whyItMatters:
      "The Browser Agent lets Longhand work on approved sites you're already signed into. Chrome provides the connection. Longhand keeps the approvals and allowed-site rules.",
    actions: [
      "Use Google Chrome on the same computer as Longhand. Keep the Longhand app open.",
      "In Longhand, open Settings → Browser Agent.",
      "If the extension says Disconnected, use the install button to open the official Longhand Agent page in the Chrome Web Store.",
      "Choose Add to Chrome and approve the install. Pin the Longhand Agent icon if you want it easy to find.",
      "Go back to Longhand. The extension should connect on its own. There's no pairing code.",
      "Wait for the Extension card to say Connected. Don't add a site during onboarding unless your lead asks you to.",
    ],
    checkpoint:
      "Settings → Browser Agent shows Connected. You can also explain the rule: approvals happen in Longhand, and the agent only works on approved domains.",
    icon: Chrome,
    images: [
      {
        src: "/legitscript-longhand-onboarding/browser-agent-connected.png",
        alt: "Longhand Browser Agent settings showing the Longhand Agent Chrome extension connected",
        label: "Settings → Browser Agent",
        caption:
          "You're ready when the Extension card says Connected. No pairing code needed.",
      },
    ],
    reference: {
      title: "Make sure Chrome and Longhand are connected",
      headers: ["Expected", "Stop signal"],
      rows: [
        ["Chrome and Longhand are open on the same computer", "Another browser, phone, or computer"],
        ["The Extension card says Connected", "Disconnected or still waiting after install"],
        ["Approvals stay in Longhand", "A browser prompt asks you to widen access outside the approved flow"],
      ],
    },
    note:
      "Connected doesn't mean every site is available. LegitScript manages the pre-approved domains. Only add another site through the approved onboarding process.",
  },
  {
    id: "verify",
    title: "Verify the Analyst workspace",
    description: "Confirm you're in the LegitScript analyst view before starting work.",
    duration: "4 min",
    whyItMatters:
      "You can sign in successfully and still be in the wrong organization or view. Check the branding, My Work, and Classification queues before you start.",
    actions: [
      "Confirm you can see the LegitScript wordmark and dark-blue organization accent.",
      "Open Agents and confirm My Work appears above Classification queues.",
      "Confirm Projects is not the primary analyst work surface; shared classification work comes from the queues.",
      "If any of these checks fail, stop before starting work and send your onboarding lead a screenshot.",
    ],
    checkpoint:
      "You can point to the LegitScript brand, the My Work board, and the Classification queues in the Analyst view.",
    icon: BadgeCheck,
    images: [
      {
        src: "/legitscript-longhand-onboarding/analyst-work-board.png",
        alt: "Longhand Agents view with My Work above the Classification queues",
        label: "Analyst workspace",
        caption:
          "My Work appears above Classification queues. Live identifiers and counts are obscured.",
      },
    ],
    reference: {
      title: "Workspace acceptance check",
      headers: ["Expected", "Stop signal"],
      rows: [
        ["LegitScript wordmark and dark-blue accent", "Stock Longhand branding only"],
        ["Agents navigation", "A generic Projects-first workspace"],
        ["My Work above Classification queues", "Either surface missing"],
        ["No personal organization secret requested", "A request for a LegitScript or team API key"],
      ],
    },
  },
  {
    id: "orient",
    title: "Learn the two work surfaces",
    description: "Know where new work appears and where to follow the runs you started.",
    duration: "5 min",
    whyItMatters:
      "Classification queues show shared work that's ready to start. My Work shows the runs you've started and whether each one is Running, Failed, Needs Review, or Done.",
    actions: [
      "Use Classification queues to see the shared backlog and start the next approved batch.",
      "Use My Work to follow each run through Running, Failed, Needs Review, and Done.",
      "Open a Needs Review card to enter the field-by-field review console.",
      "Open a Failed card to read the failure before choosing a recovery action.",
    ],
    checkpoint:
      "Given any item on screen, you can say whether it is waiting to start, running, waiting on your judgement, failed, or finished. You can also say which surface owns the next action.",
    icon: LayoutDashboard,
    reference: {
      title: "Where each action happens",
      headers: ["Surface or state", "Meaning", "Your next action"],
      rows: [
        ["Classification queues", "Shared work available to the team", "Start only the queue your lead approved"],
        ["Running", "Longhand is executing the workflow", "Let it run; inspect only if it stalls"],
        ["Needs Review", "The run is deliberately waiting on a person", "Open the review console"],
        ["Failed", "The run stopped before completion", "Read the cause before retry or re-drive"],
        ["Done", "The run reached its terminal result", "Verify the supervised outcome, then clear only if appropriate"],
      ],
    },
  },
  {
    id: "start",
    title: "Start one supervised batch",
    description: "Take the next items from the queue your lead approved.",
    duration: "8 min",
    whyItMatters:
      "Starting from a queue takes the next available items and adds the runs to My Work. You don't need to select or start each record one at a time.",
    actions: [
      "Have your onboarding lead present or explicitly authorize the practice start.",
      "Locate the designated queue and read its waiting count, batch size, and estimated cost.",
      "Click the queue's Start next N sites or Start next N products button once. The button's number is the amount of work this click will take.",
      "Keep Longhand open while the runs appear in My Work and move into Running.",
      "Do not press a separate Run control for individual queue items; a successful claim starts the work automatically.",
    ],
    checkpoint:
      "One authorized click on the designated queue creates the expected runs in My Work, with no duplicate manual starts and no work taken from a different queue.",
    icon: PlayCircle,
    images: [
      {
        src: "/legitscript-longhand-onboarding/classification-queues.png",
        alt: "Longhand classification queue cards with a Start next 10 sites button",
        label: "Approved queue",
        caption:
          "The button tells you how many items this click will claim. Your approved queue and batch size may differ.",
      },
    ],
    reference: {
      title: "What happens when you start a batch",
      headers: ["You do", "Longhand does"],
      rows: [
        ["Choose the approved queue", "Takes work only from that queue"],
        ["Click the queue's Start next N button once", "Claims up to the number shown and avoids duplicate work"],
        ["Keep the app open", "Starts the matching runs"],
        ["Watch My Work", "Shows the current state of each run"],
      ],
    },
    note:
      "This is the first action that starts real processing and may eventually write to the LegitScript database. Do not perform it unsupervised during onboarding.",
  },
  {
    id: "review",
    title: "Review and correct one result",
    description: "Check each field against the evidence before the run continues.",
    duration: "12 min",
    whyItMatters:
      "Longhand pauses a run in Needs Review so you can check each field. Your decision and any correction are recorded before the workflow continues.",
    actions: [
      "Open a card in Needs Review, or use the column's review shortcut to open the next waiting run.",
      "Read the record identity, rationale, evidence links, and any jurisdiction or network context before judging the fields.",
      "Mark each field Correct or Incorrect. When a field is incorrect, enter the exact corrected value or the additional detail required.",
      "Use Mark all correct only after you have actually checked every displayed field.",
      "Choose Submit & next record or Submit & next run. Longhand merges your corrections, resumes the workflow, and advances the queue.",
      "If the evidence does not support a decision, stop and escalate instead of guessing.",
    ],
    checkpoint:
      "You complete one field-by-field review using the evidence, record any necessary correction, and submit only after every displayed field has been deliberately checked.",
    icon: ClipboardCheck,
    images: [
      {
        src: "/legitscript-longhand-onboarding/review-console.png",
        alt: "Longhand Website Classification Review with evidence and field-level Correct or Incorrect controls",
        label: "Needs Review",
        caption:
          "Read the evidence, check every field, then submit. The example record and live values are intentionally obscured.",
      },
    ],
    reference: {
      title: "Review decisions",
      headers: ["Decision", "Use it when", "Required follow-through"],
      rows: [
        ["Correct", "The evidence supports the proposed value", "No edit; continue to the next field"],
        ["Incorrect", "The proposed value is wrong or incomplete", "Enter the corrected value or missing detail"],
        ["Stop and escalate", "The evidence is missing, conflicting, or outside your authority", "Do not guess or submit; capture the record and question"],
      ],
    },
    note:
      "Submitting the review lets the workflow continue toward the LegitScript database. A quick skim doesn't count as a completed review.",
  },
  {
    id: "handoff",
    title: "Verify, recover, and hand off",
    description: "Confirm the final state and give your lead a clear handoff.",
    duration: "5 min",
    whyItMatters:
      "A Done run gives you the expected result. A Failed run gives you a reason to investigate. In either case, record the final state and give your lead enough information to approve the next step.",
    actions: [
      "Return to My Work and verify that the supervised run reaches Done. If it doesn't, capture the exact state where it stopped.",
      "If it failed, open the run and read the cause before using Try again, Retry, or Re-drive. Follow your lead's direction for the correct recovery path.",
      "Do not dismiss an active failure merely to clean the board; dismissal moves it out of the active view without fixing the cause.",
      "Complete the onboarding checklist and send your LegitScript onboarding lead any screenshot or exact error text.",
      "Wait for your lead to approve independent queue work.",
    ],
    checkpoint:
      "Your onboarding lead receives the workspace evidence, practice run, final state, review status, and any exact error. Your lead then approves the next step or assigns one.",
    icon: LifeBuoy,
    images: [
      {
        src: "/legitscript-longhand-onboarding/failed-run.png",
        alt: "Longhand failed workflow step showing the cause, evidence, and recovery context",
        label: "Failure evidence",
        caption:
          "Open the failed step and read the cause before any retry or re-drive.",
      },
    ],
    reference: {
      title: "Recovery rule",
      headers: ["State", "Safe response"],
      rows: [
        ["Done", "Record the result and ask for sign-off"],
        ["Needs Review", "Finish or escalate the field-level decision"],
        ["Failed with a clear transient cause", "Retry only with lead approval"],
        ["Failed because the run no longer owns its item", "Use Re-drive when the app offers it"],
        ["Unknown or repeated failure", "Stop; send the exact state, run, and error"],
      ],
    },
  },
];

export const LEGITSCRIPT_ONBOARDING_SLIDES: GuideSlide[] = [
  { id: "welcome", title: "LegitScript Analyst Onboarding", kind: "cover", tone: "dark" },
  { id: "workspace-overview", title: "The analyst workspace", kind: "custom", tone: "dark" },
  { id: "operating-loop", title: "How analyst work moves", kind: "custom", tone: "blue" },
  { id: "outcomes", title: "What you'll be able to do", kind: "outcomes", tone: "paper" },
  { id: "roadmap", title: "Your onboarding roadmap", kind: "roadmap", tone: "blue" },

  { id: "prepare", title: "Prepare your invitation", kind: "activity", tone: "paper", stepId: "prepare", showWhy: true, showDeliverables: true, showNote: true },
  { id: "prepare-checkpoint", title: "Your setup details are confirmed", kind: "checkpoint", tone: "blue", stepId: "prepare" },

  { id: "install", title: "Install and open Longhand", kind: "activity", tone: "dark", stepId: "install", showWhy: true, showReference: true, showLinks: true },
  { id: "install-screen", title: "See the download fallback", heading: "If you need the download fallback", kind: "activity", tone: "dark", stepId: "install", actionIndexes: [1, 2], showImages: true, showLinks: true },
  { id: "install-checkpoint", title: "Longhand opens from the invitation", kind: "checkpoint", tone: "paper", stepId: "install" },

  { id: "join", title: "Join with your invited identity", kind: "activity", tone: "paper", stepId: "join", showWhy: true, showReference: true, showNote: true },
  { id: "join-screen", title: "See a successful organization sign-in", heading: "A successful organization sign-in looks like this", kind: "activity", tone: "paper", stepId: "join", actionIndexes: [1, 2, 3], showImages: true },
  { id: "join-checkpoint", title: "The organization sign-in landed", kind: "checkpoint", tone: "blue", stepId: "join" },

  { id: "connect-workspace", title: "Connect your Google Workspace", kind: "activity", tone: "dark", stepId: "connect-workspace", showWhy: true, showReference: true, showNote: true },
  { id: "connect-workspace-screen", title: "Confirm the Google connection", heading: "You're ready when Google Workspace says Connected", kind: "activity", tone: "dark", stepId: "connect-workspace", actionIndexes: [0, 1, 2, 4], showImages: true },
  { id: "connect-workspace-checkpoint", title: "The right Google account is connected", kind: "checkpoint", tone: "paper", stepId: "connect-workspace" },

  { id: "connect-browser-agent", title: "Install and connect the Browser Agent", kind: "activity", tone: "paper", stepId: "connect-browser-agent", showWhy: true, showReference: true, showNote: true },
  { id: "connect-browser-agent-screen", title: "Confirm the Browser Agent connection", heading: "Install it in Chrome. Confirm it in Longhand.", kind: "activity", tone: "paper", stepId: "connect-browser-agent", actionIndexes: [1, 2, 3, 4, 5], showImages: true },
  { id: "connect-browser-agent-checkpoint", title: "Chrome and Longhand are connected", kind: "checkpoint", tone: "blue", stepId: "connect-browser-agent" },

  { id: "verify", title: "Verify the Analyst workspace", kind: "activity", tone: "dark", stepId: "verify", showWhy: true, showReference: true },
  { id: "verify-screen", title: "See the Analyst workspace", heading: "This is the Analyst workspace", kind: "activity", tone: "dark", stepId: "verify", actionIndexes: [0, 1], showImages: true },
  { id: "verify-checkpoint", title: "You are in the right workspace", kind: "checkpoint", tone: "paper", stepId: "verify" },

  { id: "orient", title: "Learn the two work surfaces", kind: "activity", tone: "paper", stepId: "orient", showWhy: true, showReference: true },
  { id: "orient-checkpoint", title: "You can route the next action", kind: "checkpoint", tone: "blue", stepId: "orient" },

  { id: "start", title: "Start one supervised batch", kind: "activity", tone: "dark", stepId: "start", showWhy: true, showReference: true, showNote: true },
  { id: "start-screen", title: "See the approved queue", heading: "Start from the approved queue", kind: "activity", tone: "dark", stepId: "start", actionIndexes: [1, 2, 3], showImages: true },
  { id: "start-checkpoint", title: "The expected runs appear in My Work", kind: "checkpoint", tone: "paper", stepId: "start" },

  { id: "review", title: "Review and correct one result", kind: "activity", tone: "paper", stepId: "review", showWhy: true, showReference: true, showNote: true },
  { id: "review-screen", title: "See the review screen", heading: "Check the evidence before the run continues", kind: "activity", tone: "paper", stepId: "review", actionIndexes: [1, 2, 4], showImages: true },
  { id: "review-checkpoint", title: "Your review is recorded", kind: "checkpoint", tone: "orange", stepId: "review" },

  { id: "handoff", title: "Verify, recover, and hand off", kind: "activity", tone: "dark", stepId: "handoff", showWhy: true, showReference: true },
  { id: "handoff-screen", title: "Read the failure details", heading: "When a run stops, capture the reason before you retry", kind: "activity", tone: "dark", stepId: "handoff", actionIndexes: [0, 1, 2], showImages: true },
  { id: "handoff-checklist", title: "Complete your onboarding check", kind: "custom", tone: "blue", stepId: "handoff" },
  { id: "handoff-checkpoint", title: "Your lead can verify the outcome", kind: "checkpoint", tone: "paper", stepId: "handoff" },
];
